import { useMemo } from 'react';

import { usePointExpirationScheduleQuery } from '@ocx/graphql';
import { useConfiguration } from '@ocx/data-configuration';

import { groupExpirationsByDate, DEFAULT_NOTIFICATION_DAYS } from '../utils/group-expirations-by-date';
import { useDismissal } from './use-dismissal.hook';

export const POINT_EXPIRATION_DISPLAY_DISMISSED_AT = 'POINT_EXPIRATION_DISPLAY_DISMISSED_AT';
const POINT_EXPIRATION_SCHEDULE_LIMIT = 10;

export const usePointsExpiringSoon = () => {
  const { config } = useConfiguration();
  const notificationDays = config.loyaltyProgram.pointExpiration.notificationDays ?? DEFAULT_NOTIFICATION_DAYS;
  const { dismissed, handleManualDismissal } = useDismissal(notificationDays);

  // Skip query if:
  // - Point expiration is not enabled, OR
  // - We're still checking dismissal status (null), OR
  // - Banner was dismissed recently (true)
  const { data, loading, error } = usePointExpirationScheduleQuery({
    variables: { first: POINT_EXPIRATION_SCHEDULE_LIMIT },
    skip: !config.loyaltyProgram.pointExpiration.enabled || dismissed !== 'NOT_DISMISSED',
  });

  return useMemo(() => {
    // Only show the banner when the dismissed status indicates that we need to show it
    // and point expiration schedule data was successfully loaded
    if (dismissed !== 'NOT_DISMISSED' || loading || error) {
      return {
        pointsExpiringSoon: 0,
        shouldShowExpiringPoints: false,
        handleManualDismissal,
      };
    }

    if (
      !data?.currentCustomer?.membership?.pointExpirationSchedule?.edges ||
      data.currentCustomer.membership.pointExpirationSchedule.edges.length === 0
    ) {
      return {
        pointsExpiringSoon: 0,
        shouldShowExpiringPoints: false,
        handleManualDismissal,
      };
    }

    const schedule = data.currentCustomer.membership.pointExpirationSchedule.edges
      .map((edge) => edge.node)
      .filter((node) => node && node.points > 0);

    const { expiringSoon } = groupExpirationsByDate(schedule, notificationDays);

    const pointsExpiringSoon = expiringSoon.reduce((sum: number, item: { points: number }) => sum + item.points, 0);

    return {
      pointsExpiringSoon,
      shouldShowExpiringPoints: pointsExpiringSoon > 0 && dismissed === 'NOT_DISMISSED',
      handleManualDismissal,
    };
  }, [data, loading, error, notificationDays, dismissed, handleManualDismissal]);
};
